import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { track } from '@/lib/analytics.js';
import { Icon } from '@/ui/icons.js';
import { HeroFigure } from './HeroFigure.js';
import { StoreButtons } from './StoreButtons.js';
import { CTA, FAQ, FEATURES, HERO, LEGAL_LINKS, PRIVACY_POINTS, SHOTS, SITE_URL, STEPS } from './content.js';

/**
 * Web landing page (/landing): what the app does and where to get it.
 *
 * Outside the app shell and the sign-in guard, and the splash skips it (it has
 * its own intro). The root is always the app, so the native shell, which
 * opens at the root, never shows this page.
 *
 * The body does not scroll (it is an app shell), so this page scrolls itself.
 */

/** When the hero's figure has landed upright (HeroFigure timings), the copy follows. */
const CONTENT_DELAY_MS = 1600;

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Fades children up the first time they scroll into view. */
function Reveal({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(reducedMotion);
  useEffect(() => {
    if (shown || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [shown]);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(24px)',
        transition: `opacity 500ms ease-out ${delay}ms, transform 500ms ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/** Hero copy: waits for the intro, then fades up in sequence. */
function Intro({ children, step }: { children: ReactNode; step: number }): ReactNode {
  const [shown, setShown] = useState(reducedMotion);
  useEffect(() => {
    if (shown) return;
    const timer = window.setTimeout(() => setShown(true), CONTENT_DELAY_MS + step * 100);
    return () => window.clearTimeout(timer);
  }, [shown, step]);
  return (
    <div
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(18px)',
        transition: 'opacity 450ms ease-out, transform 450ms ease-out',
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }): ReactNode {
  return <h2 className="mb-6 text-center text-2xl font-extrabold tracking-tight sm:text-3xl">{children}</h2>;
}

/**
 * Phones and tablets can stand at your side; a desktop's webcam faces you, so
 * the web app's side view cannot work there. iPadOS reports a Mac user agent,
 * hence the touch-point check.
 */
function canStandAtYourSide(): boolean {
  return (
    window.matchMedia('(pointer: coarse)').matches ||
    navigator.maxTouchPoints > 1 ||
    /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent)
  );
}

/** On a phone: open the web app. On a desktop: a link to the app, to open on the phone. */
function WebAppLink({ from, onDark = false }: { from: 'hero' | 'cta'; onDark?: boolean }): ReactNode {
  const [phone] = useState(canStandAtYourSide);
  const tone = onDark ? 'text-white/90' : 'text-ink';
  if (!phone) {
    return (
      <a
        href={SITE_URL}
        className={`inline-flex min-h-11 items-center text-sm font-semibold underline underline-offset-4 ${tone}`}
      >
        Open Erectus on your phone
      </a>
    );
  }
  return (
    <Link
      to="/"
      onClick={() => track('landing_cta_click', { cta_id: `web_app_${from}` })}
      className={`inline-flex min-h-11 items-center gap-1 text-sm font-semibold underline underline-offset-4 ${tone}`}
    >
      Try it in your browser
    </Link>
  );
}

export function LandingPage(): ReactNode {
  useEffect(() => {
    const previous = document.title;
    document.title = 'Erectus — sitting posture, measured over the hour';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <main
      className="h-dvh overflow-x-hidden overflow-y-auto bg-surface text-ink"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden px-4 pb-20 pt-6">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(90%_60%_at_50%_40%,#ffffff_0%,transparent_70%),linear-gradient(180deg,var(--color-surface)_0%,#e8f3ec_75%,var(--color-surface)_100%)]"
          aria-hidden
        />
        <div className="relative w-full">
          <HeroFigure />
        </div>
        <div className="relative mt-4 flex flex-col items-center text-center">
          <Intro step={0}>
            <h1 className="text-5xl font-extrabold tracking-tight sm:text-6xl">{HERO.title}</h1>
          </Intro>
          <Intro step={1}>
            <p className="mt-3 max-w-md whitespace-pre-line text-balance text-base leading-relaxed text-ink-soft sm:text-lg">
              {HERO.lead}
            </p>
          </Intro>
          <Intro step={2}>
            <div className="mt-7 flex flex-col items-center gap-2">
              <StoreButtons from="hero" />
              <WebAppLink from="hero" />
            </div>
          </Intro>
        </div>
        <a
          href="#how"
          aria-label="How it works"
          className="absolute inset-x-0 bottom-4 mx-auto flex size-11 animate-bounce items-center justify-center text-muted motion-reduce:animate-none"
        >
          <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </a>
      </section>

      {/* ── How it works ─────────────────────────────────── */}
      <section id="how" className="mx-auto max-w-3xl scroll-mt-4 px-4 py-14">
        <SectionTitle>How it works</SectionTitle>
        <ol className="grid gap-3 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 80}>
              <li className="flex h-full items-start gap-4 rounded-2xl border border-line bg-card p-4 shadow-sm sm:flex-col sm:items-center sm:text-center">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-state-good text-base font-extrabold text-white">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-bold">{step.title}</h3>
                  <p className="mt-1 text-sm text-muted">{step.body}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* ── Screens ──────────────────────────────────────── */}
      <section className="py-10">
        <SectionTitle>Inside the app</SectionTitle>
        <p className="-mt-3 mb-5 text-center text-xs text-muted lg:hidden">Swipe →</p>
        <div className="flex snap-x snap-mandatory gap-5 overflow-x-auto px-[24vw] pb-6 [scrollbar-width:none] sm:px-8 lg:justify-center [&::-webkit-scrollbar]:hidden">
          {SHOTS.map((shot) => (
            <figure key={shot.file} className="w-[52vw] max-w-[220px] shrink-0 snap-center lg:w-[180px]">
              <img
                src={`/landing/${shot.file}.webp`}
                alt={shot.caption}
                width={595}
                height={1240}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full drop-shadow-xl"
              />
              <figcaption className="mt-3 text-center text-sm font-bold">{shot.caption}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-4 py-12">
        <SectionTitle>More than a single reading</SectionTitle>
        <div className="grid gap-3 sm:grid-cols-2">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 2) * 80}>
              <div className="h-full rounded-2xl border border-line bg-card p-5 shadow-sm">
                <h3 className="font-bold">{f.title}</h3>
                <p className="mt-1 text-sm text-muted">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Privacy ──────────────────────────────────────── */}
      <section className="px-4 py-12">
        <Reveal className="mx-auto max-w-xl rounded-3xl bg-ink px-6 py-9 text-white shadow-lg">
          <h2 className="text-center text-2xl font-extrabold tracking-tight sm:text-3xl">
            Video stays on your phone
          </h2>
          <ul className="mt-6 space-y-3">
            {PRIVACY_POINTS.map((point) => (
              <li key={point} className="flex gap-3 text-sm leading-relaxed text-white/90">
                <Icon name="check" size={20} strokeWidth={2.4} className="mt-0.5 shrink-0 text-[#4ade80]" />
                {point}
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section className="mx-auto max-w-xl px-4 py-12">
        <SectionTitle>Questions</SectionTitle>
        <div className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
          {FAQ.map((item) => (
            <details key={item.q} className="group p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-semibold [&::-webkit-details-marker]:hidden">
                {item.q}
                <Icon name="plus" size={18} className="shrink-0 text-muted transition group-open:rotate-45" />
              </summary>
              <p className="mt-2 text-sm leading-relaxed text-muted">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── Last call ────────────────────────────────────── */}
      <section className="px-4 pb-10 pt-4">
        <Reveal className="relative mx-auto max-w-xl overflow-hidden rounded-3xl bg-state-good px-6 py-10 text-center text-white shadow-lg">
          <h2 className="relative whitespace-pre-line text-2xl font-extrabold leading-snug sm:text-3xl">{CTA.title}</h2>
          <p className="relative mt-2 text-sm text-white/90">{CTA.body}</p>
          <div className="relative mt-6 flex flex-col items-center gap-2">
            <StoreButtons from="cta" onDark />
            <WebAppLink from="cta" onDark />
          </div>
        </Reveal>
      </section>

      <footer className="px-4 pb-10 text-center text-xs text-muted">
        <nav className="flex flex-wrap justify-center gap-x-4">
          {LEGAL_LINKS.map((l) => (
            <Link key={l.href} to={l.href} className="py-2 underline-offset-4 hover:underline">
              {l.title}
            </Link>
          ))}
        </nav>
        <p className="mt-1">Not a medical device. For wellness and educational use only.</p>
        <p className="mt-1">© 2026 uhz LLC</p>
      </footer>
    </main>
  );
}
